import React, {
    useCallback, useEffect, useId, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import apiService from '../../../services/apiService';
import {
    Button, Dialog, EmptyState, Tooltip,
} from '../../ui';
import { NOT_FOR_COMMITTED } from '../../../utils/committedUnit';
import { dealStatusView } from '../../../utils/dealStatus';
import IronSightMark from '../../shared/IronSightMark';
import UserSearch from '../../shared/UserSearch';
import { formatCurrency } from '../../../utils/formatting';
import SOURCE_LABELS from '../../../config/sourceLabels';
import {
    THINKING_STEPS, STEP_DURATIONS, stepState, fmtNum, fmtPct, percentileBucket, trendBucket, trendArrow,
    marketplaceRows, marketplaceTrends, marketplaceBadges, marketplaceInsights, resolveRiskBadge, riskBadgeTitle,
    cachedLabel, peerVerdictArrow, mismatchText, agentTaskUsers, taskPrefill, buildTaskPayload,
} from '../../../utils/askIronSight';
import { RefreshIcon } from '../icons';
import { tabCtxShape } from './tabContext';
import './AskTabV2.css';

const RISK_TONE = { High: 'crit', Medium: 'warn', Low: 'ok' };
const URGENCY_TIP = 'The agent\'s own call on how soon to act. Separate from the model\'s sell-speed risk score.';
const TASK_TYPES = [
    ['price_change', 'Price Change'],
    ['photo_update', 'Photo Update'],
    ['description', 'Description Improvement'],
    ['other', 'Other'],
];

const lower = (v) => String(v || '').toLowerCase().replace(/\s+/g, '-');

// ── U6 "What the agent checked": today's 8 thinking steps ───────────────
// While the call runs it is the thinking loader (P-UNIT-044): each step
// done / active / pending on today's timing, details shown once reached, the
// last step waits. Once the answer is in, every step is ticked.
function AgentSteps({ running }) {
    const [step, setStep] = useState(0);
    useEffect(() => {
        if (!running || step >= THINKING_STEPS.length - 1) return undefined;
        const t = setTimeout(() => setStep((s) => s + 1), STEP_DURATIONS[step]);
        return () => clearTimeout(t);
    }, [running, step]);
    const at = running ? step : THINKING_STEPS.length;
    return (
        <div className="u2-card" data-testid="ask-steps">
            <div className="u2-c-head"><h3>{running ? 'Working on it' : 'What the agent checked'}</h3></div>
            <ol className="u2-ask-steps">
                {THINKING_STEPS.map((s, i) => {
                    const st = stepState(i, at);
                    return (
                        <li key={s.label} className={`is-${st}`} aria-current={st === 'active' ? 'step' : undefined}>
                            <span className="u2-ask-ic" aria-hidden="true" />
                            <span>
                                {s.label}
                                {st !== 'pending' && <small>{s.detail}</small>}
                                <span className="v2-sr"> ({st === 'done' ? 'done' : st === 'active' ? 'in progress' : 'waiting'})</span>
                            </span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
AgentSteps.propTypes = { running: PropTypes.bool };

function RiskPill({ badge }) {
    if (!badge) return null;
    return (
        <Tooltip content={riskBadgeTitle(badge)}>
            <span className={`u2-ask-risk u2-ask-risk--${RISK_TONE[badge.level] || 'grey'}`} tabIndex={0} data-testid="ask-risk">
                <i aria-hidden="true" />
                {badge.level} risk
                {badge.pct != null && <span className="u2-ask-risk-pc">{badge.pct}%</span>}
            </span>
        </Tooltip>
    );
}
RiskPill.propTypes = { badge: PropTypes.object };

// ── U6 "Risk vs urgency": what the two numbers mean, from the real fields ──
function RiskVsUrgency({ agent, badge }) {
    return (
        <div className="u2-card" data-testid="ask-risk-urgency">
            <div className="u2-c-head"><h3>Risk vs urgency</h3></div>
            <p className="u2-note">
                {badge ? <RiskPill badge={badge} /> : <b>Not scored</b>}
                {' '}comes from the sell-speed model, the same number as in the inventory table.
            </p>
            <p className="u2-note">
                <b>Action urgency</b> is the agent&apos;s judgement on timing
                {agent?.urgency ? <>. Here it is <b>{agent.urgency}</b>{agent.urgency_reason ? `: ${agent.urgency_reason}` : '.'}</> : '; this answer did not set one.'}
            </p>
        </div>
    );
}
RiskVsUrgency.propTypes = { agent: PropTypes.object, badge: PropTypes.object };

function PercentileRow({ row }) {
    if (!row.block) return null;
    const { subject, cohort_median: median, percentile } = row.block;
    const digits = row.digits || 0;
    const bucket = percentileBucket(percentile, row.reverse);
    const left = percentile != null ? `${Math.max(0, Math.min(100, percentile))}%` : '0%';
    return (
        <div className="u2-ask-pr">
            <span>{row.label}</span>
            <span className="u2-ask-pr-s">{fmtNum(subject, digits)}{row.unit || ''}</span>
            <span className="u2-ask-pr-m">med {fmtNum(median, digits)}{row.unit || ''}</span>
            {percentile == null ? (
                <span className="u2-ask-pr-na" title="Insufficient cohort data">—</span>
            ) : (
                <span
                    className={`u2-ask-pctl u2-ask-pctl--${bucket}`}
                    role="img"
                    aria-label={`${percentile.toFixed(0)}th percentile`}
                    title={`${percentile.toFixed(0)}th percentile`}
                >
                    <span style={{ width: left }} />
                    <em style={{ left }} />
                </span>
            )}
        </div>
    );
}
PercentileRow.propTypes = { row: PropTypes.object.isRequired };

function MarketplaceCard({ mp }) {
    if (!mp) return null;
    const rows = marketplaceRows(mp);
    const badges = marketplaceBadges(mp);
    const insights = marketplaceInsights(mp);
    return (
        <section className="u2-card" aria-labelledby="u2-ask-mp">
            <div className="u2-c-head">
                <h3 id="u2-ask-mp">Marketplace performance</h3>
                <span className="u2-sub">benchmarked against {fmtNum(mp.cohort_size)} same-make/model/year peers</span>
            </div>
            {badges.length > 0 && (
                <div className="u2-ask-badges">
                    {badges.map((b) => <span key={b.key} className={`u2-ask-mpb u2-ask-mpb--${b.key}`}>{b.label}</span>)}
                </div>
            )}
            <div className="u2-ask-mpcols">
                <div>
                    <h4 className="u2-ask-h4">{SOURCE_LABELS.marketplace} (30d)</h4>
                    {rows.marketplace.map((r) => <PercentileRow key={r.label} row={r} />)}
                </div>
                <div>
                    <h4 className="u2-ask-h4">{SOURCE_LABELS.website} (30d)</h4>
                    {rows.website.map((r) => <PercentileRow key={r.label} row={r} />)}
                </div>
            </div>
            <div>
                <span className="u2-eyebrow">7 days vs prior 7</span>
                <div className="u2-ask-trends">
                    {marketplaceTrends(mp).map((t) => (
                        <span key={t.label} className={`u2-ask-trend u2-ask-trend--${trendBucket(t.value)}`}>
                            {t.label} <b>{trendArrow(t.value)} {fmtPct(t.value)}</b>
                        </span>
                    ))}
                </div>
            </div>
            {insights.length > 0 && (
                <div className="u2-ask-strip">
                    {insights.map((i) => <div key={i.key} className={`u2-ask-strip--${i.key}`}>{i.text}</div>)}
                </div>
            )}
        </section>
    );
}
MarketplaceCard.propTypes = { mp: PropTypes.object };

// ── Create task from a recommendation (P-UNIT-054) ──────────────────────
function CreateTaskDialog({
    rec, agent, unit, users, onClose, onCreated,
}) {
    const uid = useId().replace(/:/g, '');
    const pre = taskPrefill(rec, agent);
    const [type, setType] = useState(pre.type);
    const [title, setTitle] = useState(pre.title);
    const [notes, setNotes] = useState(pre.notes);
    const [description, setDescription] = useState(pre.description);
    const [suggestedPrice, setSuggestedPrice] = useState(pre.suggestedPrice);
    const [targetContact, setTargetContact] = useState(pre.targetContact);
    const [assignee, setAssignee] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [creating, setCreating] = useState(false);
    const busy = useRef(false);

    const submit = async () => {
        if (busy.current) return; // a double click sends one POST
        busy.current = true;
        setCreating(true);
        try {
            await apiService.createActivity(buildTaskPayload(unit, {
                type, title, notes, description, suggestedPrice, assignee, dueDate,
            }));
            onCreated(rec, assignee);
        } catch (err) {
            console.error('Failed to create task from recommendation', err); // eslint-disable-line no-console
            busy.current = false;
            setCreating(false);
        }
    };

    const id = (k) => `${uid}-${k}`;
    return (
        <Dialog
            open
            onClose={onClose}
            title={`Create task · #${unit.StockNumber || unit.stock_number}`}
            footer={(
                <>
                    <Button onClick={onClose}>Discard</Button>
                    <Button variant="primary" onClick={submit} disabled={creating}>
                        {creating ? 'Creating...' : 'Create task'}
                    </Button>
                </>
            )}
        >
            <div className="u2-ask-form">
                <p className="u2-ask-from">From Ask IronSight recommendation {rec.priority}</p>
                <label className="u2-ask-fl" htmlFor={id('type')}>
                    <span>Type</span>
                    <select id={id('type')} className="v2-sel" value={type} onChange={(e) => setType(e.target.value)}>
                        {TASK_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </label>
                <label className="u2-ask-fl" htmlFor={id('title')}>
                    <span>Title</span>
                    <textarea id={id('title')} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={800} rows={3} />
                </label>
                {type === 'price_change' && (
                    <div className="u2-ask-two">
                        <label className="u2-ask-fl" htmlFor={id('cur')}>
                            <span>Current price</span>
                            <input id={id('cur')} type="text" value={formatCurrency(unit.Best_Sell_Price || unit.price)} disabled />
                        </label>
                        <label className="u2-ask-fl" htmlFor={id('sug')}>
                            <span>Suggested price</span>
                            <input
                                id={id('sug')}
                                type="number"
                                value={suggestedPrice}
                                onChange={(e) => setSuggestedPrice(e.target.value)}
                                placeholder="e.g. 350000"
                                min={0}
                                step={1000}
                            />
                        </label>
                    </div>
                )}
                {targetContact && (
                    <label className="u2-ask-fl" htmlFor={id('contact')}>
                        <span>Target contact</span>
                        <input id={id('contact')} type="text" value={targetContact} onChange={(e) => setTargetContact(e.target.value)} />
                    </label>
                )}
                <label className="u2-ask-fl" htmlFor={id('notes')}>
                    <span>Notes</span>
                    <textarea id={id('notes')} value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
                </label>
                {type === 'description' && description && (
                    <label className="u2-ask-fl" htmlFor={id('desc')}>
                        <span>AI-generated description (will be included in task)</span>
                        <textarea id={id('desc')} className="u2-ask-desc-in" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} />
                    </label>
                )}
                <div className="u2-ask-two">
                    <div className="u2-ask-fl">
                        <span>Assign to</span>
                        <UserSearch users={users} value={assignee} onChange={setAssignee} placeholder="Type to search..." />
                    </div>
                    <label className="u2-ask-fl" htmlFor={id('due')}>
                        <span>Due date</span>
                        <input id={id('due')} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                    </label>
                </div>
            </div>
        </Dialog>
    );
}
CreateTaskDialog.propTypes = {
    rec: PropTypes.object.isRequired,
    agent: PropTypes.object,
    unit: PropTypes.object.isRequired,
    users: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
};

/**
 * Ask IronSight on the full unit page (P-UNIT-044…060, D9, U6). Today's
 * prefetch, loader, cached timestamp, Regenerate and "Try again" — no
 * "prepared overnight" or streaming (U5, out of scope). Main column: the
 * agent's answer; side column: what the agent checked and what risk vs
 * urgency mean.
 *
 * A committed unit has no agent: its advice (price, sell speed, retarget) is
 * about selling the unit, and this one already has a buyer.
 */
export default function AskTabV2({ ctx }) {
    if (ctx.committed) {
        const deal = dealStatusView(ctx.unit);
        return (
            <EmptyState
                title={NOT_FOR_COMMITTED}
                message={`Ask IronSight advises on selling a unit. This one is ${deal ? deal.label.toLowerCase() : 'committed'}: it already has a buyer.`}
            />
        );
    }
    return <AskTabLive ctx={ctx} />;
}
AskTabV2.propTypes = { ctx: tabCtxShape.isRequired };

function AskTabLive({ ctx }) {
    const {
        unit, detail, readOnly, showToast, activities,
    } = ctx;
    const agent = detail.fastTrackAgent;
    const loading = detail.fastTrackLoading;
    const error = detail.fastTrackError;
    const [users, setUsers] = useState([]);
    const [taskRec, setTaskRec] = useState(null);
    const [created, setCreated] = useState(() => new Set());
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        apiService.getActivityUsers().then((d) => setUsers(agentTaskUsers(d.users))).catch(() => {});
    }, []);

    const copyDescription = useCallback(() => {
        if (!agent?.suggested_description || !navigator.clipboard) return;
        navigator.clipboard.writeText(agent.suggested_description).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
    }, [agent?.suggested_description]);

    const onCreated = (rec, assignee) => {
        setCreated((prev) => new Set([...prev, rec.priority]));
        setTaskRec(null);
        showToast({ text: `Task created${assignee ? ` and assigned to ${assignee}` : ''}` });
        activities?.fetchActivities?.({ silent: true });
    };

    const stamp = cachedLabel(agent);
    const badge = resolveRiskBadge(agent, unit);
    const unitLabel = [unit?.YEAR_MANUF, unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ');

    let status;
    if (loading) status = <><i className="is-live" /><b>Generating…</b> · you can keep using the other tabs</>;
    else if (error) status = <><i className="is-error" /><b>Generation failed</b></>;
    else if (agent) status = <><i />{stamp ? <b>{stamp}</b> : <b>Generated just now</b>}</>;
    else status = <><i className="is-live" /><b>Analysing unit data…</b></>;

    const bar = (
        <div className="u2-ask-bar">
            <span className="u2-ask-fresh" role="status">{status}</span>
            {!loading && (agent || error) && (
                <Button size="sm" onClick={detail.regenerateAgent} title="Regenerate recommendations" aria-label="Regenerate recommendations">
                    <RefreshIcon />Regenerate
                </Button>
            )}
        </div>
    );

    if (loading) {
        return (
            <>
                {bar}
                <div className="u2-ask-grid">
                    <div className="u2-card u2-card--ai u2-ask-thinking">
                        <span className="u2-is-mark"><IronSightMark size={12} />IronSight is thinking</span>
                        {unitLabel && <p className="u2-note">{unitLabel}</p>}
                        <div className="v2-skel" aria-hidden="true">
                            <span className="v2-sk" style={{ width: '70%' }} />
                            <span className="v2-sk" style={{ width: '92%' }} />
                            <span className="v2-sk" style={{ width: '60%' }} />
                        </div>
                    </div>
                    <aside className="u2-ask-side" aria-label="How this answer is built"><AgentSteps running /></aside>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                {bar}
                <div className="u2-card" role="alert">
                    <div className="u2-c-head"><h3>Could not generate recommendations</h3></div>
                    <p className="u2-note">{error}</p>
                    <div><Button variant="primary" onClick={detail.regenerateAgent}>Try again</Button></div>
                </div>
            </>
        );
    }

    if (!agent) {
        return (
            <>
                {bar}
                <div className="u2-card u2-ask-empty">
                    <span className="u2-ask-pulse" aria-hidden="true" />
                    <p className="u2-note">Analysing unit data…</p>
                </div>
            </>
        );
    }

    const verdict = agent.performance_verdict;
    const lc = agent.listing_comparison;
    const below = (a, b) => (a < (b || 0) ? 'u2-ask-below' : undefined);

    return (
        <>
            {bar}
            <div className="u2-ask-grid">
                <div className="u2-col">
                    <section className="u2-ask-verdict" aria-labelledby="u2-ask-head">
                        <div className="u2-ask-lead">
                            <span className="u2-is-mark"><IronSightMark size={12} />Agent verdict</span>
                            {verdict && <span className={`u2-ask-vb u2-ask-vb--${lower(verdict)}`}>{verdict}</span>}
                            <RiskPill badge={badge} />
                            {agent.urgency && (
                                <Tooltip content={URGENCY_TIP}>
                                    <span className={`u2-ask-urg u2-ask-urg--${lower(agent.urgency)}`} tabIndex={0}>
                                        Action urgency: <b>{agent.urgency}</b>
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                        <p className="u2-ask-head" id="u2-ask-head">{agent.headline}</p>
                        {agent.urgency_reason && <p className="u2-ask-why">{agent.urgency_reason}</p>}
                    </section>

                    {agent.root_causes?.length > 0 && (
                        <section className="u2-card" aria-labelledby="u2-ask-rc">
                            <div className="u2-c-head"><h3 id="u2-ask-rc">Why the agent says this</h3></div>
                            <div className="u2-ask-rcs">
                                {agent.root_causes.map((rc, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div key={i} className="u2-ask-rc">
                                        <span className="u2-ask-rc-d" aria-hidden="true" />
                                        <div>
                                            <div className="u2-ask-rc-t">
                                                {rc.driver}
                                                <span className={`u2-ask-imp u2-ask-imp--${lower(rc.impact)}`}>{rc.impact}</span>
                                            </div>
                                            <p>{rc.detail}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {agent.recommendations?.length > 0 && (
                        <section className="u2-card" aria-labelledby="u2-ask-recs">
                            <div className="u2-c-head"><h3 id="u2-ask-recs">Recommendations</h3></div>
                            <ol className="u2-ask-recs">
                                {agent.recommendations.map((rec) => (
                                    <li key={rec.priority} className={rec.type === 'marketplace' ? 'is-mkt' : undefined}>
                                        <span className="u2-ask-no" aria-hidden="true">{rec.priority}</span>
                                        <span className="u2-ask-a">
                                            {rec.action}
                                            {rec.type && (
                                                <span className="u2-ask-rtype">
                                                    <i className={`u2-ask-rtype--${rec.type}`} aria-hidden="true" />
                                                    {rec.type}
                                                </span>
                                            )}
                                        </span>
                                        <p>{rec.rationale}</p>
                                        {!readOnly && (
                                            <span className="u2-ask-rbtn">
                                                {created.has(rec.priority) ? (
                                                    <span className="u2-ask-done">✓ Task created</span>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setTaskRec(rec)}
                                                        aria-label={`Create task from recommendation ${rec.priority}`}
                                                    >
                                                        Create task
                                                    </Button>
                                                )}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </section>
                    )}

                    <MarketplaceCard mp={agent.marketplace_performance} />

                    {agent.peer_analysis?.length > 0 && (
                        <section className="u2-card" aria-labelledby="u2-ask-peers">
                            <div className="u2-c-head"><h3 id="u2-ask-peers">Peer breakdown</h3></div>
                            {agent.peer_insight && <p className="u2-note">{agent.peer_insight}</p>}
                            <div className="u2-ask-peers">
                                {agent.peer_analysis.map((peer) => (
                                    <div key={peer.stock_number} className="u2-ask-peer">
                                        <div className="u2-ask-peer-mt">
                                            <b>#{peer.stock_number}</b>
                                            <span>${peer.asking_price?.toLocaleString('en-US')}</span>
                                            <span>{peer.hours?.toLocaleString('en-US')} hrs</span>
                                            <span>{peer.days_in_stock}d</span>
                                            <span>{peer.image_count} photos</span>
                                            <span>{peer.quote_count} quote{peer.quote_count === 1 ? '' : 's'}</span>
                                            <span className={`u2-ask-pv u2-ask-pv--${lower(peer.performance_vs_subject)}`}>
                                                {peerVerdictArrow(peer.performance_vs_subject)} {peer.performance_vs_subject}
                                            </span>
                                        </div>
                                        <p>{peer.key_differentiator}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {lc && (
                        <section className="u2-card" aria-labelledby="u2-ask-lc">
                            <div className="u2-c-head"><h3 id="u2-ask-lc">Listing comparison</h3></div>
                            <div className="u2-tw">
                                <table className="u2-tt">
                                    <thead>
                                        <tr><th aria-label="Measure" /><th className="r">This unit</th><th className="r">Peer avg</th></tr>
                                    </thead>
                                    <tbody>
                                        {lc.subject_score != null && (
                                            <tr>
                                                <td>Listing quality score</td>
                                                <td className={['r', below(lc.subject_score, lc.peer_avg_score)].filter(Boolean).join(' ')}>{lc.subject_score}/6</td>
                                                <td className="r">{lc.peer_avg_score?.toFixed(1)}/6</td>
                                            </tr>
                                        )}
                                        {lc.subject_description_length != null && (
                                            <tr>
                                                <td>Description length</td>
                                                <td className={['r', below(lc.subject_description_length, lc.peer_avg_description_length)].filter(Boolean).join(' ')}>{lc.subject_description_length} chars</td>
                                                <td className="r">{lc.peer_avg_description_length} chars</td>
                                            </tr>
                                        )}
                                        {lc.subject_options_count != null && (
                                            <tr>
                                                <td>Options listed</td>
                                                <td className={['r', below(lc.subject_options_count, lc.peer_avg_options_count)].filter(Boolean).join(' ')}>{lc.subject_options_count}</td>
                                                <td className="r">{lc.peer_avg_options_count?.toFixed(1)}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td>Listed price vs system</td>
                                            <td className={`r ${lc.price_mismatch ? 'u2-ask-mismatch' : 'u2-ask-match'}`} colSpan={2}>{mismatchText(lc)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {lc.listing_gap_summary && <p className="u2-note">{lc.listing_gap_summary}</p>}
                        </section>
                    )}

                    {agent.market_context && (
                        <section className="u2-card" aria-labelledby="u2-ask-mc">
                            <div className="u2-c-head"><h3 id="u2-ask-mc">Market context</h3></div>
                            <p className="u2-ask-mctx">{agent.market_context}</p>
                        </section>
                    )}

                    {agent.suggested_description && (
                        <section className="u2-card" aria-labelledby="u2-ask-desc">
                            <div className="u2-c-head">
                                <h3 id="u2-ask-desc">Suggested {SOURCE_LABELS.listingLower} description</h3>
                                <span className="u2-right">
                                    <Button size="sm" onClick={copyDescription}>{copied ? '✓ Copied' : 'Copy'}</Button>
                                </span>
                            </div>
                            <p className="u2-ask-descbox">{agent.suggested_description}</p>
                        </section>
                    )}
                </div>

                <aside className="u2-ask-side" aria-label="How this answer was built">
                    <AgentSteps running={false} />
                    <RiskVsUrgency agent={agent} badge={badge} />
                </aside>
            </div>

            {taskRec && (
                <CreateTaskDialog
                    rec={taskRec}
                    agent={agent}
                    unit={unit}
                    users={users}
                    onClose={() => setTaskRec(null)}
                    onCreated={onCreated}
                />
            )}
        </>
    );
}

AskTabLive.propTypes = { ctx: tabCtxShape.isRequired };
